<template>
  <div v-if="subsidyPrograms">
    <!-- TODO: These selectors need to be removed from component level and made global -->
    <v-row
      v-if="subsidyPrograms.length > 1"
      id="interested-index-subsidy-row"
      :class="{
        'bg-primary ma-0 pt-4': !store.state.site.enable_provider_v2,
        'bg-white bb-1 bc-very-light-grey': store.state.site.enable_provider_v2,
      }"
    >
      <v-col
        :class="{
          'd-flex justify-center py-6': !store.state.site.enable_provider_v2,
          'ms-5 pb-5 pt-0': store.state.site.enable_provider_v2,
        }"
      >
        <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledSelect -->
        <v-select
          v-model="selectedSubsidyProgram"
          @update:model-value="handleSubsidyProgramChange"
          :density="store.state.site.enable_provider_v2 ? 'compact' : 'comfortable'"
          :item-title="(subsidyProgram) => subsidyProgram.name"
          :items="subsidyPrograms"
          class="mxw-400"
          data-testid="subsidy-program-select"
          item-value="id"
          variant="solo"
          hide-details
          return-object
        />
      </v-col>
    </v-row>

    <v-container
      class="mt-6"
      fluid
    >
      <v-alert
        v-if="displayCocVerification"
        border="start"
        border-color="primary"
        class="mb-4"
        color="white"
        prominent
      >
        <template #prepend>
          <v-icon
            color="primary"
            icon="notifications_none"
            size="24"
          />
        </template>
        <div class="d-flex flex-md-row flex-column align-left align-md-center justify-between">
          <div class="flex-grow-1 mb-4 mb-md-0">
            <MarkdownContent
              :content="
                $bct(
                  selectedSubsidyProgram.coc_verification_alert_banner ||
                    'You have prioritized children to review. Please ensure all children are verified.',
                )
              "
            />
          </div>
          <div class="w-66 w-m-33 text-right">
            <StandardButton
              @click="handleSortByUnverified()"
              :variant="'outlined'"
              append-icon="chevron_right"
              color="primary"
              text="Filter by unverified"
            />
          </div>
        </div>
      </v-alert>

      <v-card
        border
        tile
      >
        <v-card-title
          class="fs-28"
          role="heading"
        >
          {{ $bct(store.state.pages.Interested.title || 'Interested') }}
        </v-card-title>

        <v-card-text class="pa-0">
          <v-tabs
            v-if="showWaitlistRequests"
            v-model="tab"
            @update:model-value="tabChange"
            bg-color="surface"
            grow
          >
            <v-tab value="all">
              {{ $bct('All') }}
            </v-tab>

            <v-tab value="waitlist">
              <div class="d-flex align-center ga-4">
                <span>
                  {{ $bct('Waitlist requests') }}
                </span>
              </div>
            </v-tab>
          </v-tabs>

          <v-window
            v-model="tab"
            class="border-t pa-4"
          >
            <v-window-item value="all">
              <InterestedTable
                v-if="providers !== null && subsidyPrograms !== null"
                :display-coc-verification="displayCocVerification"
                :filter-options="filters"
                :providers="providers"
                :selected-subsidy-program="selectedSubsidyProgram"
              />
            </v-window-item>

            <v-window-item
              v-if="showWaitlistRequests"
              value="waitlist"
            >
              <WaitlistRequestIndex :providers="providers" />
            </v-window-item>
          </v-window>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script setup>
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

import InterestedTable from '@/manager/components/InterestedTable.vue';
import MarkdownContent from '@/shared/components/MarkdownContent.vue';
import useApi from '@/shared/composables/useApi';
import useRouterHelper from '@/shared/composables/useRouterHelper';
import WaitlistRequestIndex from '@/manager/components/WaitlistRequestIndex.vue';

const { api: Api } = useApi();
const route = useRoute();
const store = useStore();
const { updateQuery } = useRouterHelper();
const $bct = inject('translator');

const isInitialLoad = ref(true);
const filters = ref(null);
const providers = ref(null);
const selectedSubsidyProgram = ref(null);
const subsidyPrograms = ref(null);
const tab = ref('all');

const displayCocVerification = computed(() => {
  const allProvidersDisabled =
    providers.value?.length &&
    providers.value?.every((provider) =>
      store.state.profile.disable_coc_verification_provider_ids.includes(provider.id),
    );

  return (
    selectedSubsidyProgram.value?.enable_provider_verification_of_coc_children &&
    !allProvidersDisabled
  );
});

const showWaitlistRequests = computed(() => {
  return store.state.pages.Interested.features.enable_create_new;
});

async function load() {
  const tabNames = ['all'];
  if (showWaitlistRequests.value) tabNames.push('waitlist');
  await tabChange(tabNames.includes(route.query.tab) ? route.query.tab : tab.value);

  await setSubsidyProgramIdQuery();
  await loadSubsidyPrograms();
  await loadProviders();
  isInitialLoad.value = false;
}

async function loadProviders() {
  const providerFilters = {};
  if (selectedSubsidyProgram.value) {
    providerFilters.subsidy_program_id = selectedSubsidyProgram.value.id;
  }

  const response = await Api.manager.provider.index(providerFilters);
  providers.value = response?.data;
}

async function loadSubsidyPrograms() {
  if (subsidyPrograms.value) return;

  const response = await Api.shared.subsidyProgram.index();
  subsidyPrograms.value = response.data;

  if (!selectedSubsidyProgram.value && !!store.state.filters.subsidy?.subsidyProgramId) {
    selectedSubsidyProgram.value = subsidyPrograms.value.find(
      (subsidyProgram) => subsidyProgram.id === store.state.filters.subsidy.subsidyProgramId,
    );
    await setSubsidyProgramIdQuery();
  } else if (route.query.subsidyProgramId) {
    selectedSubsidyProgram.value = subsidyPrograms.value.find(
      (subsidyProgram) => subsidyProgram.id === route.query.subsidyProgramId,
    );
  } else {
    [selectedSubsidyProgram.value] = subsidyPrograms.value;
    await setSubsidyProgramIdQuery();
  }
}

async function setSubsidyProgramIdQuery() {
  if (!selectedSubsidyProgram.value) return;

  await updateQuery({ subsidyProgramId: selectedSubsidyProgram.value.id });
  store.commit('setFilters', ['subsidy', { subsidyProgramId: selectedSubsidyProgram.value.id }]);
}

async function tabChange(newValue) {
  tab.value = newValue;
  await updateQuery({ tab: tab.value });
}

async function handleSubsidyProgramChange() {
  // Prevent system test flakes from load being called before finished running the first time
  if (!isInitialLoad.value) await load();
}

function handleSortByUnverified() {
  filters.value = {
    provider_id: undefined,
    program_id: undefined,
    status: [],
    sort_dir: 'asc',
    sort_field: 'waitlist',
    page: 1,
    verification_status: 'unverified',
  };
}

onMounted(load);
</script>

<style scoped>
:deep(.v-alert--prominent > .v-alert__prepend::after) {
  background: none !important;
}

@media (max-width: 600px) {
  :deep(.v-alert--prominent .v-alert__prepend) {
    align-self: start;
    align-items: start;
  }
}

#interested-index-subsidy-row {
  position: sticky;
  top: var(--v-layout-top, 64px);
  z-index: 4;
}
</style>
